import { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Stack, Grid } from '@mui/material';
import dayjs from 'dayjs';
import TodoCard from './TodoCard';
import HistoryCard from './HistoryCard';
import InfoSidebar from './InfoSidebar';
import ContactSidebar from './ContactSidebar';
import { sxConfig } from './workspace.style';
import { AutoCompleteSelectUI, FormLabelUI } from '../../Interface';
import useParam from '../../../context/ParamContext';
import workspaceService from '../../../service/CRM/workspace.service';
import { useLoader } from '../../../provider/LoaderProvider';
import { generateAutocompleteItems } from '../../../utils/helper';
import CrmTaskDialog from './CrmTaskDialog/CrmTaskDialog';

export const WorkspacePage = ({ workspaceState, selectedProjectId }) => {
  const { showLoader, hideLoader } = useLoader();
  const { params } = useParam();
  const { autoCompleteOptions, autoCompleteData } = workspaceState;
  const [selectedValues, setSelectedValues] = useState({});
  const [refetch, setRefetch] = useState(false);

  const defCrmTaskDialogData = {
    open: false,
    projectId: null,
    companyId: null,
  };
  const [crmTaskDialogData, setCrmTaskDialogData] = useState(defCrmTaskDialogData);

  useEffect(() => {
    getProjectById();
  }, [selectedProjectId]);

  useEffect(() => {
    if (refetch) getProjectById();
  }, [refetch]);

  const showProjectTypeStatus = params.PROJECT_SHOW_PROJECT_TYPE_STATUS === 'Y';

  const statusFieldName = showProjectTypeStatus ? 'projectTypeStatusId' : 'statusId';

  const filteredStatusOptions = useMemo(() => {
    if (!showProjectTypeStatus) {
      return autoCompleteOptions.status;
    }

    const statusForProjectType = autoCompleteData?.status?.filter(
      (status) => status.projectTypeId === selectedValues.projectTypeId
    );

    return generateAutocompleteItems(statusForProjectType, 'name', 'id');
  }, [selectedValues.projectTypeId, params]);

  const getProjectById = () => {
    if (!selectedProjectId) {
      setSelectedValues({});
      return;
    }

    showLoader();
    workspaceService
      .getWorkspaceData(selectedProjectId)
      .then((data) => {
        setSelectedValues(data);
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleNewCrmTask = () => {
    setCrmTaskDialogData((prev) => ({
      ...prev,
      open: true,
      projectId: selectedValues.projectId,
      companyId: selectedValues.companyId,
    }));
  };

  return (
    <>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={10}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ ...sxConfig.card, px: 3, py: 2.5 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {selectedValues.projectName}
                    </Typography>

                    <Box sx={{ width: { xs: '100%', md: 250 }, flexShrink: 0 }}>
                      <AutoCompleteSelectUI
                        label={<FormLabelUI text="Státusz" />}
                        variant="outlined"
                        size="small"
                        dataset={filteredStatusOptions}
                        selectedValue={selectedValues[statusFieldName]}
                      />
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} color="text.secondary" sx={{ flexShrink: 0 }}>
                    <Typography>Módosítva:</Typography>
                    <Typography>{dayjs(selectedValues.updatedAt).format('YYYY-MM-DD HH:mm')}</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <TodoCard
                  selectedValues={selectedValues}
                  autoCompleteOptions={autoCompleteOptions}
                  autoCompleteData={autoCompleteData}
                  setRefetch={setRefetch}
                  handleNewCrmTask={handleNewCrmTask}
                />
                <HistoryCard handleNewCrmTask={handleNewCrmTask} />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoSidebar autoCompleteOptions={autoCompleteOptions} selectedValues={selectedValues} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={2}>
          <ContactSidebar selectedValues={selectedValues} />
        </Grid>
      </Grid>

      <CrmTaskDialog
        dialogData={crmTaskDialogData}
        handleClose={() => setCrmTaskDialogData(defCrmTaskDialogData)}
        autoCompleteOptions={autoCompleteOptions}
        autoCompleteData={autoCompleteData}
        setRefetch={setRefetch}
      />
    </>
  );
};
